import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useLocation } from "wouter";
import { BookmarkHintBubble } from "@/components/bookmark-hint-bubble";
import { MapPin, Briefcase, Clock, ShieldCheck, Flag, ExternalLink, Linkedin, MessageSquare, Mail, Bookmark, Bell } from "lucide-react";
import MarkdownContent from "@/components/markdown-content";
import { isActivelySeekingRecent, ACTIVELY_SEEKING_WINDOW_MS } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useTrackEvent } from "@/hooks/use-track-event";
import { useReportProfile, useSaveProfile, useUnsaveProfile, usePatchRecruiterMePreferences, useSaveAlumniProfile, useUnsaveAlumniProfile } from "@workspace/api-client-react";
import type { Profile } from "@workspace/api-client-react";

interface ProfileCardProps {
  profile: Profile;
  viewerIsVerified?: boolean;
  viewerProfileId?: number | null;
  viewerHasDiscord?: boolean;
  recruiterSavedIds?: Set<number>;
  alumniSavedIds?: Set<number>;
  onSaveToggle?: (profileId: number, saved: boolean) => void;
  onDisciplineClick?: (discipline: string) => void;
  onLocationClick?: (location: string) => void;
  showBookmarkHint?: boolean;
  onBookmarkHintDismiss?: () => void;
}

export default function ProfileCard({ profile, viewerIsVerified = false, viewerProfileId, viewerHasDiscord, recruiterSavedIds, alumniSavedIds, onSaveToggle, onDisciplineClick, onLocationClick, showBookmarkHint = false, onBookmarkHintDismiss }: ProfileCardProps) {
  const [, navigate] = useLocation();
  const [reportOpen, setReportOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [alertsDiscoveryOpen, setAlertsDiscoveryOpen] = useState(false);

  const [seekingActive, setSeekingActive] = useState(() =>
    isActivelySeekingRecent(profile.activelySeekingUpdatedAt),
  );

  useEffect(() => {
    const ts = profile.activelySeekingUpdatedAt;
    if (!ts) {
      setSeekingActive(false);
      return;
    }
    const parsedMs = new Date(ts).getTime();
    if (Number.isNaN(parsedMs)) {
      setSeekingActive(false);
      return;
    }
    const expiresAt = parsedMs + ACTIVELY_SEEKING_WINDOW_MS;

    let timerId: ReturnType<typeof setTimeout>;

    const scheduleCheck = () => {
      const msRemaining = expiresAt - Date.now();
      if (msRemaining <= 0) {
        setSeekingActive(false);
        return;
      }
      setSeekingActive(true);
      // JS setTimeout silently wraps at 2^31-1 ms (~24.8 days), which is less
      // than the 30-day window. Clamp to a safe ceiling and recurse until expiry.
      const MAX_SAFE_DELAY_MS = 2_000_000_000;
      timerId = setTimeout(scheduleCheck, Math.min(msRemaining, MAX_SAFE_DELAY_MS));
    };

    scheduleCheck();
    return () => clearTimeout(timerId);
  }, [profile.activelySeekingUpdatedAt]);
  const { toast } = useToast();
  const trackEvent = useTrackEvent();
  const reportMutation = useReportProfile();
  const saveMutation = useSaveProfile();
  const unsaveMutation = useUnsaveProfile();
  const preferencesMutation = usePatchRecruiterMePreferences();
  const saveAlumniMutation = useSaveAlumniProfile();
  const unsaveAlumniMutation = useUnsaveAlumniProfile();

  const isRecruiter = recruiterSavedIds !== undefined;
  const isAlumniBookmarker = alumniSavedIds !== undefined && !isRecruiter;
  const isRecruiterSaved = recruiterSavedIds?.has(profile.id) ?? false;
  const isAlumniSaved = alumniSavedIds?.has(profile.id) ?? false;
  const isSaved = isRecruiter ? isRecruiterSaved : isAlumniSaved;
  const showBookmark = isRecruiter || isAlumniBookmarker;

  const handleSaveToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!showBookmark) return;

    if (isRecruiter) {
      if (isSaved) {
        unsaveMutation.mutate(
          { profileId: profile.id },
          {
            onSuccess: () => {
              onSaveToggle?.(profile.id, false);
              toast({ title: "Profile removed", description: `${profile.name} removed from your saved list.` });
            },
            onError: () => {
              toast({ title: "Error", description: "Failed to unsave profile.", variant: "destructive" });
            },
          },
        );
      } else {
        saveMutation.mutate(
          { profileId: profile.id },
          {
            onSuccess: (data) => {
              onSaveToggle?.(profile.id, true);
              if (data.savedCountBefore === 0) {
                setAlertsDiscoveryOpen(true);
              } else {
                toast({ title: "Profile saved", description: `${profile.name} added to your saved list.` });
              }
            },
            onError: () => {
              toast({ title: "Error", description: "Failed to save profile.", variant: "destructive" });
            },
          },
        );
      }
    } else {
      // Alumni bookmark
      if (isSaved) {
        unsaveAlumniMutation.mutate(
          { profileId: profile.id },
          {
            onSuccess: () => {
              onSaveToggle?.(profile.id, false);
              toast({ title: "Bookmark removed", description: `${profile.name} removed from your saved list.` });
            },
            onError: () => {
              toast({ title: "Error", description: "Failed to remove bookmark.", variant: "destructive" });
            },
          },
        );
      } else {
        saveAlumniMutation.mutate(
          { profileId: profile.id },
          {
            onSuccess: () => {
              onSaveToggle?.(profile.id, true);
              toast({ title: "Profile bookmarked", description: `${profile.name} added to your saved list.` });
            },
            onError: () => {
              toast({ title: "Error", description: "Failed to bookmark profile.", variant: "destructive" });
            },
          },
        );
      }
    }
  };

  const getInitials = (name: string) => {
    return name.split(" ").map((n) => n[0]).join("").substring(0, 2).toUpperCase();
  };

  const getTenureString = () => {
    const tenures = profile.riotTenures;
    if (!tenures || tenures.length === 0) return null;
    return tenures
      .map((t) => `${t.start}–${t.end ?? "Present"}`)
      .join(", ");
  };

  const firstPortfolioLink = profile.portfolioLinks
    ? profile.portfolioLinks.split(/[|,\n]/)[0]?.trim() || null
    : null;

  const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.closest("a") || target.closest("button")) return;
    navigate(`/profile/${profile.id}`);
  };

  const handleReport = (e: React.MouseEvent) => {
    e.stopPropagation();
    setReportOpen(true);
  };

  const submitReport = (e: React.MouseEvent) => {
    e.stopPropagation();
    reportMutation.mutate(
      { id: profile.id, data: { reason: reason.trim() || null } },
      {
        onSuccess: () => {
          setReportOpen(false);
          setReason("");
          toast({ title: "Report submitted", description: "Thank you for helping keep the directory accurate." });
        },
        onError: () => {
          toast({ title: "Error", description: "Failed to submit report. Please try again.", variant: "destructive" });
        },
      },
    );
  };

  return (
    <div className="relative h-full group">
      <motion.div
        whileHover={{ y: -4 }}
        whileTap={{ scale: 0.98 }}
        transition={{ type: "spring", stiffness: 400, damping: 25 }}
        className="bg-zinc-900/60 border border-white/10 hover:border-primary/50 transition-[border-color,box-shadow] duration-300 rounded-xl p-6 h-full flex flex-col relative overflow-hidden group-hover:shadow-[0_0_30px_-5px_rgba(220,20,60,0.15)] cursor-pointer"
        onClick={handleCardClick}
      >
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

        <div className="flex items-start gap-4 mb-5">
          {profile.linkedinUrl ? (
            <a
              href={profile.linkedinUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="shrink-0"
            >
              <Avatar className="w-16 h-16 border-2 border-white/5 rounded-lg shadow-xl">
                <AvatarImage src={profile.avatarUrl || undefined} alt={profile.name} className="object-cover rounded-md" />
                <AvatarFallback className="bg-zinc-800 text-zinc-300 font-bold rounded-md font-sans">
                  {getInitials(profile.name)}
                </AvatarFallback>
              </Avatar>
            </a>
          ) : (
            <Avatar className="w-16 h-16 border-2 border-white/5 rounded-lg shadow-xl shrink-0">
              <AvatarImage src={profile.avatarUrl || undefined} alt={profile.name} className="object-cover rounded-md" />
              <AvatarFallback className="bg-zinc-800 text-zinc-300 font-bold rounded-md font-sans">
                {getInitials(profile.name)}
              </AvatarFallback>
            </Avatar>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              {profile.linkedinUrl ? (
                <a
                  href={profile.linkedinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="font-bold text-lg text-white leading-snug break-words group-hover:text-primary transition-colors hover:underline underline-offset-2"
                >
                  {profile.name}
                </a>
              ) : (
                <h3 className="font-bold text-lg text-white leading-snug break-words group-hover:text-primary transition-colors">
                  {profile.name}
                </h3>
              )}
              {profile.verified && <ShieldCheck className="w-4 h-4 text-cyan-400 shrink-0" />}
            </div>
            <p className="text-sm text-zinc-400 font-medium font-sans truncate">{profile.title}</p>

            {(seekingActive || profile.openToRelocation) && (
              <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                {seekingActive && (
                  <span className="inline-flex items-center gap-1 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider">
                    <Briefcase className="w-2.5 h-2.5" />
                    Open to Work
                  </span>
                )}
                {profile.openToRelocation && (
                  <span className="inline-flex items-center gap-1 bg-zinc-800/50 text-zinc-400 border border-white/10 px-1.5 py-0.5 rounded text-[10px] font-medium">
                    <MapPin className="w-2.5 h-2.5" />
                    Open to Relocation
                  </span>
                )}
              </div>
            )}

            <div className="flex items-center gap-x-3 gap-y-2 flex-wrap mt-2 text-xs text-zinc-500 font-sans">
              {profile.location && (
                <div className="flex items-center gap-1 min-w-0">
                  <MapPin className="w-3 h-3 shrink-0" />
                  <span
                    className={`truncate max-w-[180px] shrink-0${onLocationClick ? " cursor-pointer hover:text-zinc-300 transition-colors" : ""}`}
                    onClick={onLocationClick ? (e) => { e.stopPropagation(); onLocationClick(profile.location!); } : undefined}
                  >{profile.location}</span>
                </div>
              )}
              {getTenureString() && (
                <div className="flex items-center gap-1">
                  <Briefcase className="w-3 h-3" />
                  <span>{getTenureString()}</span>
                </div>
              )}
              {profile.industryExperience && (
                <div className="flex items-center gap-1 text-zinc-200">
                  <Clock className="w-3 h-3 shrink-0" />
                  <span>{profile.industryExperience}</span>
                </div>
              )}
            </div>
            <div className="mt-2">
              <Badge
                variant="secondary"
                className={`bg-zinc-800/50 text-zinc-300 border border-white/5 font-sans rounded text-xs px-2 py-0.5${onDisciplineClick ? " cursor-pointer hover:bg-zinc-700/80 hover:text-primary transition-colors" : " hover:bg-zinc-800/80"}`}
                onClick={onDisciplineClick ? (e) => { e.stopPropagation(); onDisciplineClick(profile.discipline); } : undefined}
              >
                {profile.discipline}
              </Badge>
            </div>
          </div>
        </div>

        <div className="flex-1 flex flex-col gap-4 font-sans">
          {((profile.experienceBlocks ?? []).length > 0) && (() => {
            const allBlocks = profile.experienceBlocks!;
            const MAX_BLOCKS = 3;
            const visibleBlocks = allBlocks.slice(0, MAX_BLOCKS);
            const remaining = allBlocks.length - visibleBlocks.length;
            return (
              <div className="text-sm flex-1 flex flex-col min-h-0">
                <span className="text-zinc-500 text-xs uppercase tracking-wider block mb-1 shrink-0 font-semibold">
                  Experience
                </span>
                <div className="relative overflow-hidden max-h-60">
                  {visibleBlocks.map((block, i) => (
                    <div key={block.id ?? i} className={i > 0 ? "mt-2" : ""}>
                      {block.title && (
                        <p className="text-zinc-300 text-xs font-semibold mb-0.5">{block.title}</p>
                      )}
                      <div className="relative max-h-24 overflow-hidden">
                        <MarkdownContent className="text-zinc-400 text-xs leading-relaxed">{block.description}</MarkdownContent>
                        <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-zinc-900/90 to-transparent pointer-events-none" />
                      </div>
                    </div>
                  ))}
                  <div className="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-t from-zinc-900/80 to-transparent pointer-events-none" />
                </div>
                {remaining > 0 && (
                  <p className="mt-1.5 shrink-0 text-[10px] uppercase tracking-wider text-zinc-600 font-semibold">
                    +{remaining} more {remaining === 1 ? "role" : "roles"}
                  </p>
                )}
              </div>
            );
          })()}
        </div>

        <div
          className="mt-5 pt-5 border-t border-white/5 flex flex-col gap-2"
          onClickCapture={(e) => {
            const el = e.target as HTMLElement;
            if (el.closest("a, button")) {
              trackEvent("profile_cta_click", { profileId: profile.id });
            }
          }}
        >
          {(() => {
            const isVerifiedViewer = viewerIsVerified || profile.id === viewerProfileId;

            const primaryCls = "flex items-center justify-center gap-1.5 text-xs font-semibold text-white bg-primary hover:bg-primary/90 px-3 py-2 rounded-md transition-colors w-full";
            const ghostCls = "flex items-center justify-center gap-1.5 text-xs font-semibold text-zinc-400 hover:text-white bg-transparent border border-white/10 hover:border-white/20 px-3 py-2 rounded-md transition-colors w-full";
            const tap = { scale: 0.93 };
            const spring = { type: "spring", stiffness: 400, damping: 20 } as const;

            type CtaItem = { key: string; el: React.ReactNode };

            if (!isVerifiedViewer) {
              const ctas: CtaItem[] = [];

              if (profile.linkedinUrl) {
                ctas.push({
                  key: "linkedin",
                  el: (
                    <motion.a
                      href={profile.linkedinUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      whileTap={tap}
                      transition={spring}
                      className={primaryCls}
                    >
                      <Linkedin className="w-3 h-3 shrink-0" />
                      LinkedIn
                    </motion.a>
                  ),
                });
              }

              if (firstPortfolioLink) {
                ctas.push({
                  key: "portfolio",
                  el: (
                    <motion.button
                      whileTap={tap}
                      transition={spring}
                      onClick={(e) => { e.stopPropagation(); navigate(`/profile/${profile.id}#portfolio`); }}
                      className={ghostCls}
                    >
                      <ExternalLink className="w-3 h-3 shrink-0" />
                      View Portfolio
                    </motion.button>
                  ),
                });
              }

              if (ctas.length === 0) return null;
              if (ctas.length === 1) return <div>{ctas[0].el}</div>;
              const total = ctas.length;
              const remaining = total - 1;
              return (
                <div className="grid grid-cols-2 gap-2">
                  {ctas.map((cta, idx) => {
                    const spanFull = idx === 0 || (idx === total - 1 && remaining % 2 !== 0);
                    return (
                      <div key={cta.key} className={spanFull ? "col-span-2" : ""}>
                        {cta.el}
                      </div>
                    );
                  })}
                </div>
              );
            }

            const ctas: CtaItem[] = [];

            if (profile.showDiscordToPeers && profile.discordId) {
              ctas.push({
                key: "discord",
                el: (
                  <motion.a
                    href={`https://discordapp.com/users/${profile.discordId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    whileTap={tap}
                    transition={spring}
                    className={ctas.length === 0 ? primaryCls : ghostCls}
                  >
                    <MessageSquare className="w-3 h-3 shrink-0" />
                    Message on Discord
                  </motion.a>
                ),
              });
            }

            if (profile.email) {
              ctas.push({
                key: "email",
                el: (
                  <motion.a
                    href={`mailto:${encodeURIComponent(profile.email)}`}
                    onClick={(e) => e.stopPropagation()}
                    whileTap={tap}
                    transition={spring}
                    className={ctas.length === 0 ? primaryCls : ghostCls}
                  >
                    <Mail className="w-3 h-3 shrink-0" />
                    Send Email
                  </motion.a>
                ),
              });
            }

            if (profile.linkedinUrl) {
              ctas.push({
                key: "linkedin",
                el: (
                  <motion.a
                    href={profile.linkedinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    whileTap={tap}
                    transition={spring}
                    className={ctas.length === 0 ? primaryCls : ghostCls}
                  >
                    <Linkedin className="w-3 h-3 shrink-0" />
                    LinkedIn
                  </motion.a>
                ),
              });
            }

            if (firstPortfolioLink) {
              ctas.push({
                key: "portfolio",
                el: (
                  <motion.button
                    whileTap={tap}
                    transition={spring}
                    onClick={(e) => { e.stopPropagation(); navigate(`/profile/${profile.id}#portfolio`); }}
                    className={ctas.length === 0 ? primaryCls : ghostCls}
                  >
                    <ExternalLink className="w-3 h-3 shrink-0" />
                    View Portfolio
                  </motion.button>
                ),
              });
            }

            if (ctas.length === 0) return null;
            if (ctas.length === 1) return <div>{ctas[0].el}</div>;
            const total = ctas.length;
            const remaining = total - 1;

            return (
              <div className="grid grid-cols-2 gap-2">
                {ctas.map((cta, idx) => {
                  const spanFull = idx === 0 || (idx === total - 1 && remaining % 2 !== 0);
                  return (
                    <div key={cta.key} className={spanFull ? "col-span-2" : ""}>
                      {cta.el}
                    </div>
                  );
                })}
              </div>
            );

          })()}
          {viewerProfileId != null && !viewerHasDiscord && profile.id !== viewerProfileId && profile.showDiscordToPeers && profile.discordId && (
            <motion.div
              whileTap={{ scale: 0.93 }}
              transition={{ type: "spring", stiffness: 400, damping: 20 }}
            >
              <Link
                href="/dashboard"
                onClick={(e) => e.stopPropagation()}
                className="flex items-start gap-2.5 w-full text-left p-2.5 rounded-lg border border-indigo-500/15 bg-indigo-500/5 hover:bg-indigo-500/10 hover:border-indigo-500/25 transition-colors group/discord"
              >
                <MessageSquare className="w-3.5 h-3.5 text-indigo-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-semibold text-indigo-300 group-hover/discord:text-indigo-200 transition-colors">
                    Connect Discord to message members
                  </p>
                  <p className="text-[10px] text-zinc-500 mt-0.5">Link your account on your dashboard</p>
                </div>
              </Link>
            </motion.div>
          )}
        </div>
      </motion.div>

      {showBookmark && (
        <>
          <button
            onClick={(e) => {
              if (showBookmarkHint) {
                onBookmarkHintDismiss?.();
              }
              handleSaveToggle(e);
            }}
            disabled={saveMutation.isPending || unsaveMutation.isPending || saveAlumniMutation.isPending || unsaveAlumniMutation.isPending}
            className={`absolute top-3 right-3 w-6 h-6 rounded-md border flex items-center justify-center transition-colors z-10 disabled:opacity-50 ${
              isSaved
                ? "bg-primary/10 border-primary/30 text-primary"
                : showBookmarkHint
                  ? "bg-zinc-800/80 border-white/10 text-zinc-600 hover:text-primary hover:border-primary/30"
                  : "bg-zinc-800/80 border-white/10 text-zinc-600 hover:text-primary hover:border-primary/30 opacity-0 group-hover:opacity-100"
            }`}
            title={isSaved ? "Remove from saved" : "Save profile"}
          >
            <Bookmark className={`w-3 h-3 ${isSaved ? "fill-current" : ""}`} />
          </button>
          <BookmarkHintBubble show={showBookmarkHint} onDismiss={() => onBookmarkHintDismiss?.()} />
        </>
      )}

      {!showBookmark && (
        <button
          onClick={handleReport}
          className="absolute top-3 right-3 w-6 h-6 rounded-md bg-zinc-800/80 border border-white/10 flex items-center justify-center text-zinc-600 hover:text-amber-400 hover:border-amber-400/30 transition-colors opacity-0 group-hover:opacity-100 z-10"
          title="Report this profile"
        >
          <Flag className="w-3 h-3" />
        </button>
      )}

      {reportOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget) setReportOpen(false);
          }}
        >
          <div className="bg-zinc-900 border border-white/10 rounded-xl p-6 w-full max-w-sm shadow-2xl">
            <h3 className="font-bold text-white mb-1">Report Profile</h3>
            <p className="text-sm text-zinc-400 font-sans mb-4">
              Optionally describe the issue with this profile. Our team will review it.
            </p>
            <Textarea
              placeholder="Reason (optional)"
              className="bg-zinc-800 border-white/10 text-white font-sans resize-none mb-4"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <div className="flex gap-2">
              <Button
                className="flex-1 bg-primary hover:bg-primary/90 text-white font-bold uppercase text-xs h-9"
                onClick={submitReport}
                disabled={reportMutation.isPending}
              >
                {reportMutation.isPending ? "Submitting..." : "Submit Report"}
              </Button>
              <Button
                variant="outline"
                className="border-white/10 hover:bg-white/5 font-sans h-9 px-4"
                onClick={(e) => { e.stopPropagation(); setReportOpen(false); }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={alertsDiscoveryOpen} onOpenChange={setAlertsDiscoveryOpen}>
        <DialogContent
          className="bg-zinc-950 border border-white/10 text-white max-w-sm"
          onClick={(e) => e.stopPropagation()}
        >
          <DialogHeader>
            <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center mb-2">
              <Bell className="w-5 h-5 text-primary" />
            </div>
            <DialogTitle className="text-base font-bold text-white">
              Profile saved
            </DialogTitle>
            <DialogDescription className="text-zinc-400 font-sans text-sm leading-relaxed">
              {profile.name} has been added to your shortlist. Want to be emailed when a saved
              candidate goes Open to Work?
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 mt-2">
            <Button
              className="w-full bg-primary hover:bg-primary/90 text-white font-bold uppercase text-sm tracking-wide"
              disabled={preferencesMutation.isPending}
              onClick={(e) => {
                e.stopPropagation();
                preferencesMutation.mutate(
                  { data: { receiveSavedProfileAlerts: true } },
                  {
                    onSuccess: () => {
                      setAlertsDiscoveryOpen(false);
                      toast({
                        title: "Alerts enabled",
                        description: "You will be emailed when a saved candidate goes Open to Work.",
                      });
                    },
                    onError: () => {
                      setAlertsDiscoveryOpen(false);
                      toast({ title: "Error", description: "Failed to enable alerts.", variant: "destructive" });
                    },
                  },
                );
              }}
            >
              {preferencesMutation.isPending ? "Saving..." : "Enable Alerts"}
            </Button>
            <Button
              variant="ghost"
              className="w-full text-zinc-400 hover:text-white font-sans text-sm"
              disabled={preferencesMutation.isPending}
              onClick={(e) => {
                e.stopPropagation();
                setAlertsDiscoveryOpen(false);
                toast({ title: "Profile saved", description: `${profile.name} added to your saved list.` });
              }}
            >
              Not Now
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
